import { useState } from 'react';
import { useAuPouce } from '../partage/surface';
import { motsDuDossier } from '../partage/vocabulaire';
import { ImporterPlan } from '../import-ia';
import { OuvrirUnDossier, PartirDUnModele } from './OuvrirUnDossier';
import './Premier.css';

const EXEMPLES = ['Déménagement', 'Bali cet été', 'Recruter quelqu’un', 'Préparer un voyage'];

/** Les trois temps ; « projet » au téléphone, « dossier » à l'ordinateur (`motsDuDossier`). */
function lesTemps(m: ReturnType<typeof motsDuDossier>) {
  return [
    {
      titre: 'Tu écris un nom',
      corps: m.ilExiste,
    },
    {
      titre: 'Tu descends dedans, un mot à la fois',
      corps:
        'Debout, dans le bus, au moment où tu y penses. Tu sais déjà où l’idée va — il n’y a jamais de boîte à trier plus tard.',
    },
    {
      // Ticket 14 : cette page est « Maintenant » sans projet ; le troisième temps dit ce qu'elle deviendra.
      titre: 'Ici, ce que tu peux faire maintenant',
      corps: m.desTonPremier,
    },
  ];
}

/**
 * D2 · Le premier lancement
 *
 * **Surtout pas une page blanche.** C'est ici que la §10 dit qu'on perd les gens : le coût
 * de saisie initiale et la page blanche sont le risque n°1.
 *
 * Un seul geste demandé — écrire un nom. Le champ n'est pas une boîte mais **une ligne
 * soulignée** : rien à remplir, une chose à écrire. Quatre exemples pour débloquer celui qui
 * ne sait pas par où commencer. Puis les trois temps de ce qui va se passer, côte à côte.
 * Et la promesse du bas : tu n'auras jamais à tout saisir d'un coup.
 *
 * Depuis le ticket 14 de retours-testeurs (décisions d'Igor du 2026-09-27), c'est la page
 * « Maintenant » tant qu'on n'a aucun projet : l'app s'ouvre toujours là, et un nouvel
 * utilisateur n'a rien. Le troisième temps dit donc ce que la page deviendra. « Projets »,
 * sans projet, a sa propre page (`ProjetsVides.tsx`), qui présente « Maintenant ».
 */
export function Premier({ onOuvrir, refus = null }: { onOuvrir: (titre: string) => void; refus?: string | null }) {
  const [importOuvert, setImportOuvert] = useState(false);
  const m = motsDuDossier(useAuPouce());
  const troisTemps = lesTemps(m);

  return (
    <div className="premier">
      <div className="premier-halo" aria-hidden="true" />

      <div className="premier-centre">
        <h1>Par quoi tu commences&nbsp;?</h1>
        <p className="premier-amorce">
          {m.unDossierCEst} Écris son nom&nbsp;— tu n’auras rien d’autre à remplir aujourd’hui.
        </p>

        <OuvrirUnDossier
          libelle={m.ouvrirUn}
          exemple="Acheter la maison"
          idees={EXEMPLES}
          onOuvrir={onOuvrir}
          refus={refus}
          focus
        />

        <ol className="premier-temps">
          {troisTemps.map((temps, i) => (
            // Le dernier temps parle de cette page : la visite du tuto l'éclaire, par son repère
            // `premier-temps-ici` (`zonesDuTuto.ts`).
            <li
              key={temps.titre}
              className={i === troisTemps.length - 1 ? 'premier-temps-ici' : undefined}
              data-tuto={i === troisTemps.length - 1 ? 'premier-temps-ici' : undefined}
            >
              <div className="premier-temps-tete">
                <span className="premier-numero" aria-hidden="true">
                  {i + 1}
                </span>
                <h2>{temps.titre}</h2>
              </div>
              <p>{temps.corps}</p>
            </li>
          ))}
        </ol>

        <PartirDUnModele />

        <section className="premier-modele">
          <span className="premier-modele-barre" aria-hidden="true" />
          <div className="premier-modele-dit">
            <h2>Ou importe un plan écrit par ton IA</h2>
            <p>
              Copie l’invite dans ton IA : elle te pose des questions, puis écrit ton plan. Colle
              sa réponse : les étapes, leurs conditions et leurs délais arrivent {m.dansUnNouveau}, et
              tout reste modifiable.
            </p>
          </div>
          <button type="button" onClick={() => setImportOuvert(true)}>
            Importer un plan
          </button>
        </section>

        <p className="premier-promesse">
          Il n’y a jamais de page blanche&nbsp;: tu n’auras jamais à tout saisir d’un coup, et un{' '}
          {m.aMoitieVide}
        </p>
      </div>

      {importOuvert && <ImporterPlan onFermer={() => setImportOuvert(false)} />}
    </div>
  );
}
